import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth } from './AuthContext.jsx'

const STORAGE_KEY = 'sonologue.theme'
const CHOICES = ['system', 'light', 'dark']

const ThemeContext = createContext(null)

function readStored() {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return CHOICES.includes(value) ? value : 'system'
  } catch {
    return 'system'
  }
}

function applyTheme(choice) {
  const root = document.documentElement
  if (choice === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', choice)
}

/**
 * Theme preference: system by default (DESIGN.md), overridable per account.
 *
 * localStorage is the pre-session copy — index.html reads it before first
 * paint so a dark reload does not flash light. The account column is
 * authoritative once the session loads, so the same choice follows a person
 * to another browser.
 */
export function ThemeProvider({ children }) {
  const { user, updateMe } = useAuth()
  const [theme, setThemeState] = useState(readStored)

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  useEffect(() => {
    if (user?.theme_preference && user.theme_preference !== theme) {
      setThemeState(user.theme_preference)
      try {
        localStorage.setItem(STORAGE_KEY, user.theme_preference)
      } catch {
        /* no storage available */
      }
    }
    // Only when the signed-in account changes: a local toggle must not be
    // immediately reverted by this effect before the PATCH lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.public_id, user?.theme_preference])

  const setTheme = useCallback(
    (choice) => {
      const next = CHOICES.includes(choice) ? choice : 'system'
      setThemeState(next)
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        /* no storage available */
      }
      if (user) {
        // Best effort: the local choice already applied, and a failed sync is
        // not worth an error dialog over.
        updateMe({ theme_preference: next }).catch(() => {})
      }
    },
    [user, updateMe],
  )

  const value = useMemo(() => ({ theme, setTheme, choices: CHOICES }), [theme, setTheme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}
